import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { auth, db } from '../firebase';
import { useInvitations } from '../hooks/useInvitations';

const MODE_NEW = 'new';
const MODE_LOGIN = 'login';

const StaffInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { validateToken, consumeToken } = useInvitations();

  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [eventName, setEventName] = useState('');
  const [invitation, setInvitation] = useState(null);

  const [mode, setMode] = useState(MODE_NEW);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    const check = async () => {
      const result = await validateToken(token);
      if (!result.valid) {
        setTokenError(result.error);
      } else {
        setInvitation(result.invitation);
        setEventName(result.event?.event_name || 'イベント');
      }
      setValidating(false);
    };
    check();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleRegister = async () => {
    setFormError(null);
    if (!name.trim()) return setFormError('名前を入力してください');
    if (!email.trim()) return setFormError('メールアドレスを入力してください');
    if (password.length < 8) return setFormError('パスワードは8文字以上で入力してください');
    if (password !== confirm) return setFormError('パスワードが一致しません');

    setSubmitting(true);
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;

      await set(ref(db, `users/${uid}`), {
        email,
        display_name: name.trim(),
        role: 'staff',
        invited_by: invitation.owner_id,
        created_at: Date.now(),
      });

      await consumeToken(token, uid, invitation.owner_id);
      navigate('/dashboard');
    } catch (err) {
      setFormError(getAuthError(err.code));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async () => {
    setFormError(null);
    if (!email.trim() || !password) return setFormError('メールアドレスとパスワードを入力してください');

    setSubmitting(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;

      // ロール確認
      const profileSnap = await get(ref(db, `users/${uid}`));
      const profile = profileSnap.val();
      if (!profile) {
        setFormError('このアカウントではスタッフとして参加できません');
        setSubmitting(false);
        return;
      }

      // ゲストはスタッフに昇格
      if (profile.role === 'guest') {
        await set(ref(db, `users/${uid}/role`), 'staff');
      } else if (!['staff', 'owner', 'superadmin'].includes(profile.role)) {
        setFormError('このアカウントではスタッフとして参加できません');
        setSubmitting(false);
        return;
      }

      // 既にこのイベントに参加済みか確認
      const memberSnap = await get(
        ref(db, `events/${invitation.event_id}/staff_members/${uid}`)
      );
      if (memberSnap.exists()) {
        navigate('/dashboard');
        return;
      }

      await consumeToken(token, uid, invitation.owner_id);
      navigate('/dashboard');
    } catch (err) {
      setFormError(getAuthError(err.code));
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div style={styles.root}>
        <p style={{ color: '#64748b' }}>招待リンクを確認中...</p>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorTitle}>招待リンクが無効です</h2>
          <p style={styles.errorText}>{tokenError}</p>
          <button style={styles.button} onClick={() => navigate('/login')}>
            ログインページへ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={styles.header}>
          <span style={styles.logoIcon}>🔗</span>
          <h1 style={styles.logoText}>LinkBoard</h1>
          <div style={styles.eventBadge}>📋 {eventName}</div>
          <p style={styles.subtitle}>このイベントにスタッフとして招待されています</p>
        </div>

        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(mode === MODE_NEW ? styles.tabActive : {}) }}
            onClick={() => { setMode(MODE_NEW); setFormError(null); }}
          >
            初めての方（新規登録）
          </button>
          <button
            style={{ ...styles.tab, ...(mode === MODE_LOGIN ? styles.tabActive : {}) }}
            onClick={() => { setMode(MODE_LOGIN); setFormError(null); }}
          >
            アカウントをお持ちの方
          </button>
        </div>

        <div style={styles.form}>
          {mode === MODE_NEW && (
            <>
              <Field label="名前">
                <input
                  style={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="例：田中 太郎"
                />
              </Field>
              <Field label="メールアドレス">
                <input
                  style={styles.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                />
              </Field>
              <Field label="パスワード（8文字以上）">
                <input
                  style={styles.input}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="パスワードを入力"
                />
              </Field>
              <Field label="パスワード（確認）">
                <input
                  style={styles.input}
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="もう一度入力"
                  onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                />
              </Field>
              {formError && <p style={styles.error}>{formError}</p>}
              <button
                style={{ ...styles.button, opacity: submitting ? 0.7 : 1 }}
                onClick={handleRegister}
                disabled={submitting}
              >
                {submitting ? '登録中...' : '登録してイベントに参加'}
              </button>
            </>
          )}

          {mode === MODE_LOGIN && (
            <>
              <Field label="メールアドレス">
                <input
                  style={styles.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                />
              </Field>
              <Field label="パスワード">
                <input
                  style={styles.input}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="パスワードを入力"
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
              </Field>
              {formError && <p style={styles.error}>{formError}</p>}
              <button
                style={{ ...styles.button, opacity: submitting ? 0.7 : 1 }}
                onClick={handleLogin}
                disabled={submitting}
              >
                {submitting ? 'ログイン中...' : 'ログインしてイベントに参加'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>{label}</label>
    {children}
  </div>
);

const getAuthError = (code) => {
  const map = {
    'auth/email-already-in-use': 'このメールアドレスは既に登録されています。「アカウントをお持ちの方」からログインしてください。',
    'auth/invalid-email': 'メールアドレスの形式が正しくありません',
    'auth/weak-password': 'パスワードは8文字以上で入力してください',
    'auth/invalid-credential': 'メールアドレスまたはパスワードが正しくありません',
    'auth/user-not-found': 'メールアドレスが見つかりません',
    'auth/wrong-password': 'パスワードが正しくありません',
    'auth/too-many-requests': 'ログイン失敗が続いたためブロックされました。しばらく時間をおいてから再試行してください',
  };
  return map[code] ?? 'エラーが発生しました。再度お試しください。';
};

const styles = {
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f4f6fb',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    margin: '20px',
  },
  header: { textAlign: 'center', marginBottom: '28px' },
  logoIcon: { fontSize: '36px' },
  logoText: { fontSize: '20px', fontWeight: '800', color: '#1e293b', margin: '6px 0 10px' },
  eventBadge: {
    display: 'inline-block',
    background: '#ede9fe',
    color: '#5b21b6',
    borderRadius: '20px',
    padding: '4px 14px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  subtitle: { fontSize: '13px', color: '#64748b', margin: 0 },
  tabs: {
    display: 'flex',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '24px',
  },
  tab: {
    flex: 1,
    padding: '10px 8px',
    border: 'none',
    background: '#f8fafc',
    color: '#64748b',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabActive: {
    background: '#6c63ff',
    color: '#fff',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  input: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  button: {
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    background: '#6c63ff',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '4px',
    width: '100%',
  },
  errorIcon: { fontSize: '48px', textAlign: 'center', marginBottom: '12px' },
  errorTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', textAlign: 'center' },
  errorText: { fontSize: '14px', color: '#64748b', textAlign: 'center', marginBottom: '24px' },
  error: { color: '#ef4444', fontSize: '13px', margin: 0 },
};

export default StaffInvitePage;
